import { FC, useCallback, useMemo } from 'react';
import { Typography, Cascader, theme } from 'antd';
import type { DefaultOptionType } from 'antd/es/cascader';
import { ArtifactAttachments } from '#types/System.types';
import FlexCol from '../atoms/FlexCol';

const { Text } = Typography;

/**
 * Interface for a shareable file with metadata
 */
export interface ShareableFile {
    fileId: string;
    versionId: string;
    fileName: string;
    shareUrl?: string;
    isShared: boolean;
}

/**
 * File sharing request item
 */
export interface FileSharingRequest {
    fileId: string;
    versionId: string;
    fileName: string;
}

/**
 * Result of a file sharing operation
 */
export interface ShareFilesResult {
    error?: string;
    results?: Record<string, {
        fileId: string;
        versionId: string;
        shareUrl?: string;
        error?: string;
    }>;
}

/**
 * Extended option type for the cascader component
 */
interface ExtendedOptionType extends DefaultOptionType {
    isShared?: boolean;
    fileName?: string;
    isSelectAll?: boolean;
}

/**
 * Props for the FileLinksSelector component
 */
interface FileLinksSelectorProps {
    /**
     * Array of currently selected files (source of truth)
     */
    selectedFiles: ShareableFile[];
    /**
     * Callback when selected files change
     */
    onSelectedFilesChange: (files: ShareableFile[]) => void;
    /**
     * Available attachments to select from
     */
    availableAttachments?: ArtifactAttachments;
    /**
     * Whether the component is disabled
     */
    disabled?: boolean;
}

/**
 * Fully controlled component for selecting shareable file links using a cascader.
 * The selection state is managed entirely by the parent component via `selectedFiles` prop.
 */
const FileLinksSelector: FC<FileLinksSelectorProps> = ({
    selectedFiles,
    onSelectedFilesChange,
    availableAttachments = {},
    disabled = false
}) => {
    const { token } = theme.useToken();

    /**
     * Transforms availableAttachments into cascader options with a Select All option
     */
    const cascaderOptions = useMemo<ExtendedOptionType[]>(() => {
        const fileOptions = Object.entries(availableAttachments).map(([fileId, fileData]) => {
            const versionEntries = Object.entries(fileData.versions || {});
            versionEntries.sort(([, a], [, b]) => 
                (b.versionNumber || 0) - (a.versionNumber || 0)
            );
            
            const latestVersion = versionEntries[0]?.[1];
            const fileName = latestVersion?.versionFileName || fileData.fileName || 'Unnamed file';
            const isShared = latestVersion?.share?.isShared || false;
            
            return {
                label: (
                    <Text ellipsis style={{ maxWidth: 300 }}> 
                        {fileName}
                    </Text>
                ),
                value: fileId,
                key: fileId, 
                fileName,
                isShared,
                children: versionEntries.map(([versionId, versionDetails]) => {
                    const versionIsShared = versionDetails.share?.isShared || false;
                    const versionValue = `${fileId}:${versionId}`;
                    
                    return {
                        label: (
                            <Text ellipsis style={{ maxWidth: 300 }}> 
                                v{versionDetails.versionNumber || 1}
                            </Text>
                        ),
                        value: versionValue,
                        key: versionValue, 
                        isShared: versionIsShared,
                        fileName
                    };
                })
            };
        });

        if (fileOptions.length > 0) {
            const selectAllOption: ExtendedOptionType = {
                label: <Text style={{ color: token.colorPrimary }}>Select All/None</Text>,
                value: 'select-all',
                key: 'select-all',
                isSelectAll: true,
            };
            return [...fileOptions, selectAllOption]; 
        }
        
        return fileOptions;
    }, [availableAttachments]);
    
    /**
     * Converts cascader value paths (string[][]) to ShareableFile objects
     */
    const convertToShareableFiles = useCallback((cascaderValues: string[][]) => {
        const result: ShareableFile[] = [];
        
        cascaderValues.forEach(path => {
            const leafValue = path[path.length - 1];
            
            try {
                if (leafValue.includes(':')) {
                    const [fileId, versionId] = leafValue.split(':');
                    
                    if (!fileId || !versionId) {
                        console.warn('Invalid composite key:', leafValue);
                        return;
                    }
                    
                    const attachmentFile = availableAttachments[fileId];
                    const version = attachmentFile?.versions?.[versionId];
                    
                    if (version) {
                        result.push({
                            fileId,
                            versionId,
                            fileName: version.versionFileName || attachmentFile?.fileName || 'Unnamed file',
                            shareUrl: version.share?.url,
                            isShared: version.share?.isShared || false
                        });
                    } else {
                        console.warn(`Version not found: ${fileId}/${versionId}`);
                    }
                } else {
                    if (leafValue === 'select-all') return; 
                    
                    const fileId = leafValue;
                    const attachmentFile = availableAttachments[fileId];
                    const versionsMap = attachmentFile?.versions;
                    
                    if (versionsMap) {
                        const versionEntries = Object.entries(versionsMap);
                        versionEntries.sort(([, a], [, b]) => 
                            (b.versionNumber || 0) - (a.versionNumber || 0)
                        );
                        
                        if (versionEntries.length > 0) {
                            const [versionId, version] = versionEntries[0];
                            
                            result.push({
                                fileId,
                                versionId,
                                fileName: version.versionFileName || attachmentFile?.fileName || 'Unnamed file',
                                shareUrl: version.share?.url,
                                isShared: version.share?.isShared || false
                            });
                        }
                    } else {
                        console.warn(`File not found: ${fileId}`);
                    }
                }
            } catch (error) {
                console.error('Error processing cascader value:', error, path);
            }
        });
        
        return result;
    }, [availableAttachments]);
    
    /**
     * Gets all available latest version paths in the format Cascader expects
     */
    const getAllLatestVersionPaths = useCallback(() => {
        return cascaderOptions
            .filter(option => !option.isSelectAll) 
            .map(fileOption => {
                if (!fileOption.children?.length) return null;
                return [fileOption.children[0].value as string]; 
            })
            .filter(Boolean) as string[][];
    }, [cascaderOptions]);
    
    /**
     * Handles cascader selection change. Calculates the next state and calls onSelectedFilesChange.
     */
    const handleCascaderChange = useCallback((
        values: (string | number | null)[][]
    ) => {
        if (disabled) return;

        const incomingStringValues = values.map(path => 
            path.filter(Boolean).map(val => String(val))
        ) as string[][];
        
        const selectAllClicked = incomingStringValues.some(path => 
            path.length === 1 && path[0] === 'select-all'
        );

        let nextSelectedFiles: ShareableFile[];
        
        if (selectAllClicked) {
            const allPaths = getAllLatestVersionPaths();
            const allSelectableShareableFiles = convertToShareableFiles(allPaths);
            const allCurrentlySelected = selectedFiles.length === allSelectableShareableFiles.length && 
                allSelectableShareableFiles.every(file => 
                    selectedFiles.some(sf => sf.fileId === file.fileId && sf.versionId === file.versionId)
                );

            if (allCurrentlySelected) {
                nextSelectedFiles = [];
            } else {
                nextSelectedFiles = allSelectableShareableFiles;
            }
        } else {
            const filteredValues = incomingStringValues.filter(path => 
                !(path.length === 1 && path[0] === 'select-all')
            );
            nextSelectedFiles = convertToShareableFiles(filteredValues);
        }

        onSelectedFilesChange(nextSelectedFiles);

    }, [disabled, selectedFiles, getAllLatestVersionPaths, onSelectedFilesChange, convertToShareableFiles, availableAttachments]);

    /**
     * Derive the value for the Cascader directly from the selectedFiles prop.
     * It needs to contain the full path: [fileId, 'fileId:versionId']
     */
    const cascaderValue = useMemo(() => {
        return selectedFiles.map(file => [file.fileId, `${file.fileId}:${file.versionId}`]);
    }, [selectedFiles]);
    
    return (
        <FlexCol gap={0}>
            <Text>
                Attachment Links
            </Text>
            {cascaderOptions.length > 0 ? (
                <Cascader
                    options={cascaderOptions}
                    value={cascaderValue} 
                    onChange={(values) => handleCascaderChange(values)}
                    multiple
                    maxTagCount={0}
                    maxTagPlaceholder={(omittedValues) => 
                        omittedValues.length > 0 
                            ? `${omittedValues.length} attachment${omittedValues.length !== 1 ? 's' : ''} selected` 
                            : 'Select files to share'
                    }
                    placeholder="Select files to share"
                    disabled={disabled}
                    style={{ width: '100%' }}
                    showSearch
                    showCheckedStrategy={Cascader.SHOW_PARENT}
                />
            ) : (
                <Text type="secondary">No attachments available</Text>
            )}
        </FlexCol>
    );
};

export default FileLinksSelector; 